import type { NumericSeries, ChannelDisplayOverride } from "./channelDisplay";
import type { PresentationSample } from "./positionSeries";
import { batteryCategory, BATTERY_LABELS, type PresentationProfile } from "./presentationProfiles";
import { channelValueAt } from "./valueReadout";

/** An envelope supports a category only when its complete range belongs to it.
 * Explicit source gaps remain gaps; an envelope spanning categories is never assigned a midpoint. */
export function batterySamples(series:NumericSeries[]):PresentationSample[] {
  return series.flatMap(item=>{
    if(item.representation === "native")return item.values.map((value,index)=>({
      start:item.start_seconds+index*item.sample_period_seconds,end:item.start_seconds+(index+1)*item.sample_period_seconds,
      value:batteryCategory(value),state:value===null?"Missing":"Invalid",
    }));
    return item.bins.flatMap(bin=>{
      const low=batteryCategory(bin.minimum),high=batteryCategory(bin.maximum);
      const value=bin.valid_count>0 && low!==null && low===high?low:null;
      const state=bin.valid_count<=0 || bin.minimum===null?"Missing":low===null || high===null?"Invalid":"Mixed / unavailable";
      const boundaries=[bin.start_seconds,bin.end_seconds,...(item.gaps||[]).flatMap(g=>[g.start_seconds,g.end_seconds]).filter(t=>t>bin.start_seconds&&t<bin.end_seconds)].sort((a,b)=>a-b);
      return boundaries.slice(0,-1).map((start,i)=>{
        const end=boundaries[i+1],gap=item.gaps?.some(g=>g.start_seconds<end&&g.end_seconds>start);
        return {start,end,value:gap?null:value,state:gap?"Missing":state};
      });
    });
  });
}
export function profileValueText(series:NumericSeries,seconds:number,profile:PresentationProfile,display:ChannelDisplayOverride):string {
  const readout=channelValueAt(series,seconds);
  if(profile.id!=="battery")return readout.text;
  if(series.representation === "minmax") {
    const bin=series.bins.find(b=>b.start_seconds<=seconds&&b.end_seconds>seconds);
    if(readout.text === "Not available")return "Missing";
    return bin && (batteryCategory(bin.minimum)===null||batteryCategory(bin.maximum)===null)?"Invalid battery envelope":readout.text;
  }
  const index=Math.floor((seconds-series.start_seconds)/series.sample_period_seconds+.5);
  const raw=index>=0&&index<series.sample_count?series.values[index]:null;
  if(raw==null)return "Missing";
  const category=batteryCategory(raw);
  if(category===null)return "Invalid battery value";
  return `${raw}%${display.batteryRepresentation === "categories"?` · ${BATTERY_LABELS[category]}`:""}`;
}
